import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileUp, FileText, X, Loader2, ChevronRight, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../store/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

export const JOB_STATUS = {
  uploaded: { label: "Subido", cls: "bg-slate-100 text-slate-600", step: 0 },
  extracting: { label: "Extrayendo texto", cls: "bg-sky-100 text-sky-700 border-sky-200", step: 1 },
  classifying: { label: "Clasificando con IA", cls: "bg-amber-100 text-amber-800 border-amber-200", step: 2 },
  review_pending: { label: "Pendiente de revisión", cls: "bg-indigo-100 text-indigo-700 border-indigo-200", step: 3 },
  completed: { label: "Completado", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", step: 4 },
  error: { label: "Error", cls: "bg-red-100 text-red-700 border-red-200", step: 0 },
};

const MAX_FILES = 10;
const MAX_BYTES = 20 * 1024 * 1024;
const ACCEPT = [".docx", ".pdf"];

export function ProgressBar({ status }) {
  const s = JOB_STATUS[status] || JOB_STATUS.uploaded;
  const pct = status === "error" ? 100 : (s.step / 4) * 100;
  const color = status === "error" ? "bg-red-400" : status === "completed" ? "bg-emerald-500" : "bg-primary";
  return (
    <div className="h-1.5 bg-slate-100 rounded overflow-hidden w-full">
      <div className={`h-1.5 ${color} rounded transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("es-ES"); } catch { return d; }
}

export default function Imports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canImport = ["owner", "admin", "compliance_manager"].includes(user?.role);
  const inputRef = useRef(null);

  const [selected, setSelected] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await api.get("/imports");
      setJobs(data || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/imports");
        if (!cancelled) setJobs(data || []);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Polling mientras haya jobs en progreso
  useEffect(() => {
    const active = jobs.some((j) => ["uploaded", "extracting", "classifying"].includes(j.status));
    if (!active) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/imports");
        setJobs(data || []);
      } catch { /* reintento en el siguiente ciclo */ }
    }, 4000);
    return () => clearInterval(t);
  }, [jobs]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList);
    const valid = [];
    for (const f of incoming) {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      if (!ACCEPT.includes(ext)) { toast.error(`'${f.name}': solo .docx y .pdf.`); continue; }
      if (f.size > MAX_BYTES) { toast.error(`'${f.name}' supera 20 MB.`); continue; }
      valid.push(f);
    }
    setSelected((prev) => {
      const merged = [...prev, ...valid].slice(0, MAX_FILES);
      if (prev.length + valid.length > MAX_FILES) toast.warning(`Máximo ${MAX_FILES} archivos por importación.`);
      return merged;
    });
  };

  const onDrop = (e) => { e.preventDefault(); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); };

  const removeFile = (idx) => setSelected((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (selected.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      selected.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/imports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Importación iniciada. Procesando con IA...");
      setSelected([]);
      await loadJobs();
      navigate(`/importar/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <div data-testid="imports-page" className="max-w-4xl">
      <div className="flex items-center gap-2">
        <FileUp className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">Importar SGSI existente</h1>
          <p className="text-[14px] text-slate-500 mt-0.5">Sube tus políticas, procedimientos y registros (.docx, .pdf). La IA los clasificará contra los controles y cláusulas para que tú revises y confirmes.</p>
        </div>
      </div>

      {canImport && (
        <div
          className="mt-6 border-2 border-dashed border-slate-200 rounded-lg p-8 text-center hover:border-primary/40 transition-colors"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          data-testid="import-dropzone"
        >
          <Upload className="h-8 w-8 text-slate-300 mx-auto" />
          <p className="text-[14px] text-slate-600 mt-2">Arrastra archivos aquí o</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => inputRef.current?.click()} data-testid="import-select-files">
            Seleccionar archivos
          </Button>
          <input ref={inputRef} type="file" accept=".docx,.pdf" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="import-file-input" />
          <p className="text-[12px] text-slate-400 mt-3">Hasta {MAX_FILES} archivos · máx. 20 MB cada uno · .docx y .pdf · sin OCR</p>
        </div>
      )}

      {selected.length > 0 && (
        <div className="mt-4 bg-white border border-slate-200 rounded-md p-4" data-testid="import-selected-list">
          <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">Archivos seleccionados ({selected.length})</div>
          <div className="space-y-1.5">
            {selected.map((f, idx) => (
              <div key={idx} className="flex items-center gap-2 text-[14px]">
                <FileText className="h-4 w-4 text-slate-400" />
                <span className="flex-1 text-slate-800 truncate">{f.name}</span>
                <span className="text-[12px] text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
                <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-destructive" data-testid={`remove-file-${idx}`}><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <Button className="mt-3" onClick={submit} disabled={uploading} data-testid="import-submit">
            {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Subiendo...</> : <><Upload className="h-4 w-4 mr-1" /> Subir e importar</>}
          </Button>
        </div>
      )}

      <div className="mt-8">
        <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">Importaciones</div>
        <div className="bg-white border border-slate-200 rounded-md">
          {loading && <div className="px-4 py-8 text-center text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Cargando...</div>}
          {!loading && jobs.length === 0 && <div className="px-4 py-10 text-center text-[14px] text-slate-400" data-testid="imports-empty">Aún no has importado ningún documento.</div>}
          {jobs.map((j) => {
            const st = JOB_STATUS[j.status] || JOB_STATUS.uploaded;
            const failed = (j.files || []).filter((f) => f.error).length;
            return (
              <button key={j.id} onClick={() => navigate(`/importar/${j.id}`)} className="w-full flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 text-left" data-testid={`job-row-${j.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-medium text-slate-900">{(j.files || []).length} archivo(s)</span>
                    <Badge className={st.cls}>{st.label}</Badge>
                    {failed > 0 && <span className="text-[11px] text-amber-700 inline-flex items-center gap-0.5"><AlertTriangle className="h-3 w-3" />{failed}</span>}
                  </div>
                  <div className="mt-1.5"><ProgressBar status={j.status} /></div>
                  <div className="text-[11px] text-slate-400 mt-1">{fmtDate(j.created_at)}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
